import { useEffect, useRef } from 'react';

/**
 * 🔴 THE WAY PAST THE CHROME — design-system plan DS-11a1 (§1.1; F33, F401, WCAG 2.4.1).
 *
 * The first thing a keyboard reaches in every shell (the back office, the portal, the console), so a person on a
 * keyboard or a switch does not tab through the sidebar and the bar on every page to reach the page. It is clipped
 * like `.wb-sr-only` until it has focus, and then a chip over the bar (`.wb-skip`, components.css).
 *
 * ⚠️ NOT A FRAGMENT NAVIGATION. The portal and the console route by the hash (`#/orders`, `#/licence`), so a plain
 * `<a href="#main">` would send them to their not-found page. The click is prevented and focus is MOVED to
 * `<main id="main" tabIndex={-1}>`; the `href` is only what a link with no script would do, and the address never
 * changes (skip-link.spec.tsx, on a hash router and a memory router).
 */
export function SkipLink({ label = 'Skip to content', lang }: { label?: string; lang?: string }) {
  return (
    <a
      className="wb-skip"
      href="#main"
      lang={lang}
      onClick={(e) => {
        e.preventDefault();
        focusMain();
      }}
    >
      {label}
    </a>
  );
}

/** Focus the page's `<main id="main" tabIndex={-1}>`, if the shell has drawn it. */
export function focusMain(): void {
  document.getElementById('main')?.focus();
}

/**
 * 🔴 A NEW PAGE STARTS AT THE PAGE — §1.1. On every change of `key` (the route's pathname, or the console's hash)
 * focus moves to `main`, so a screen reader is not left on the link that was clicked, now describing a page that has
 * gone, and the next Tab starts in the new page rather than back in the sidebar.
 *
 * ⚠️ NOT ON THE FIRST RENDER: the first load leaves focus where the browser put it (the skip link comes first). The
 * key is compared with the last one seen rather than counted, so React's development double effect is not a move.
 */
export function useFocusMainOnNavigate(key: string): void {
  const seen = useRef(key);
  useEffect(() => {
    if (seen.current === key) return;
    seen.current = key;
    focusMain();
  }, [key]);
}
